<script lang="ts">
  import Settings from './icons/Settings.svelte';

  interface Props {
    currentView: 'overview' | 'recurring' | 'budgets' | 'visualizations' | 'settings';
    onViewChange: (view: 'overview' | 'recurring' | 'budgets' | 'visualizations' | 'settings') => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    isMobileMenuOpen?: boolean;
    onMobileMenuClose?: () => void;
  }

  let {
    currentView,
    onViewChange,
    isCollapsed = $bindable(false),
    onToggleCollapse,
    isMobileMenuOpen = $bindable(false),
    onMobileMenuClose
  }: Props = $props();

  function handleViewChange(view: 'overview' | 'recurring' | 'budgets' | 'visualizations' | 'settings') {
    onViewChange(view);
    // Close mobile menu when a view is selected
    if (onMobileMenuClose) {
      onMobileMenuClose();
    }
  }

  function handleBackdropClick() {
    if (onMobileMenuClose) {
      onMobileMenuClose();
    }
  }
</script>

<!-- Backdrop for mobile drawer -->
{#if isMobileMenuOpen}
  <div class="backdrop" onclick={handleBackdropClick}></div>
{/if}

<aside class="sidebar" class:collapsed={isCollapsed} class:mobile-open={isMobileMenuOpen}>
  <button class="collapse-btn" onclick={onToggleCollapse} aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      {#if isCollapsed}
        <path d="M7 4L13 10L7 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      {:else}
        <path d="M13 4L7 10L13 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      {/if}
    </svg>
  </button>

  <button class="sidebar-header" onclick={onMobileMenuClose}>
    <h1 class="logo" class:hidden={isCollapsed}>Keep Finance</h1>
    <img src="/plutus-icon.png" alt="Keep Finance" class="logo-icon" class:hidden={!isCollapsed} />
  </button>

  <nav class="nav">
    <button
      class="nav-item"
      class:active={currentView === 'overview'}
      onclick={() => handleViewChange('overview')}
      aria-label="Overview"
    >
      <svg class="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor"/>
      </svg>
      {#if !isCollapsed}
        <span class="nav-label">Overview</span>
      {/if}
    </button>

    <button
      class="nav-item"
      class:active={currentView === 'recurring'}
      onclick={() => handleViewChange('recurring')}
      aria-label="Recurring"
    >
      <svg class="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      {#if !isCollapsed}
        <span class="nav-label">Recurring</span>
      {/if}
    </button>

    <button
      class="nav-item"
      class:active={currentView === 'budgets'}
      onclick={() => handleViewChange('budgets')}
      aria-label="Budgets"
    >
      <svg class="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      {#if !isCollapsed}
        <span class="nav-label">Budgets</span>
      {/if}
    </button>

    <button
      class="nav-item"
      class:active={currentView === 'visualizations'}
      onclick={() => handleViewChange('visualizations')}
      aria-label="Visualizations"
    >
      <svg class="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      {#if !isCollapsed}
        <span class="nav-label">Visualizations</span>
      {/if}
    </button>
  </nav>

  <div class="sidebar-footer">
    <button
      class="nav-item settings"
      class:active={currentView === 'settings'}
      onclick={() => handleViewChange('settings')}
      aria-label="Settings"
    >
      <Settings size={24} />
      {#if !isCollapsed}
        <span class="nav-label">Settings</span>
      {/if}
    </button>
  </div>
</aside>

<style lang="scss">
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: var(--sidebar-width);
    background: var(--gradient-header);
    color: white;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-lg);
    z-index: var(--z-dropdown);
    transition: width var(--transition-base);

    &.collapsed {
      width: var(--sidebar-width-collapsed);
    }
  }

  .collapse-btn {
    position: absolute;
    right: calc(var(--spacing-sm) * -1);
    top: var(--spacing-2xl);
    background: var(--gradient-header);
    border: none;
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    width: var(--spacing-sm);
    height: var(--spacing-3xl);
    padding: 0;
    cursor: pointer;
    color: rgba(255, 255, 255, var(--opacity-hover));
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-fast);
    box-shadow: var(--shadow-md);
    z-index: 101;

    svg {
      width: var(--spacing-md);
      height: var(--spacing-md);
      margin-left: -1px;
    }

    &:hover {
      color: white;
      box-shadow: var(--shadow-lg);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .sidebar-header {
    padding: var(--spacing-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: var(--border-width-thin) solid rgba(255, 255, 255, 0.1);
    min-height: var(--sidebar-width-collapsed);
    background: transparent;
    border-left: none;
    border-right: none;
    border-top: none;
    cursor: default;
    width: 100%;
    transition: background var(--transition-fast);
  }

  @media (max-width: 1024px) {
    .sidebar-header {
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.05);
      }
    }
  }

  .logo {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    color: white;
    white-space: nowrap;
  }

  .logo-short {
    margin: 0;
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-bold);
    color: white;
  }

  .logo-icon {
    width: var(--icon-size-xl);
    height: var(--icon-size-xl);
    object-fit: contain;
    // background: rgba(255, 255, 255, 0.15);
    border-radius: var(--radius-md);
    padding: var(--spacing-xs);
  }

  .hidden {
    display: none;
  }

  .nav {
    flex: 1;
    padding: var(--spacing-lg) 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    overflow-y: auto;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-lg);
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, var(--opacity-hover));
    cursor: pointer;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    transition: all var(--transition-fast);
    border-left: var(--border-width-thick) solid transparent;
    text-align: left;
    width: 100%;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    &.active {
      background: rgba(255, 255, 255, 0.15);
      color: white;
      border-left-color: white;
    }

    .collapsed & {
      justify-content: center;
      padding: var(--spacing-sm);
    }
  }

  .nav-icon {
    flex-shrink: 0;
    width: var(--icon-size-sm);
    height: var(--icon-size-sm);
  }

  .nav-label {
    white-space: nowrap;
    font-size: var(--font-size-sm);
  }

  .sidebar-footer {
    padding: var(--spacing-lg) 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    .nav-item.settings {
      border-left: none;
    }
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: calc(var(--z-dropdown) - 1);
    animation: fadeIn var(--transition-fast);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    .sidebar {
      transform: translateX(-100%);
      z-index: var(--z-modal);
      transition: transform var(--transition-base);
      width: var(--sidebar-width-collapsed); /* Icon-only width on mobile */

      &.mobile-open {
        transform: translateX(0);
      }

      /* Force collapsed styling on mobile */
      &.collapsed,
      &:not(.collapsed) {
        width: var(--sidebar-width-collapsed);

        &.mobile-open {
          transform: translateX(0);
        }
      }
    }

    .collapse-btn {
      display: none;
    }

    /* Force icon-only view on mobile */
    .nav-item {
      justify-content: center;
      padding: var(--spacing-sm);
    }

    .nav-label {
      display: none;
    }

    /* Force icon-only header on mobile */
    .sidebar-header .logo {
      display: none !important;
    }

    .sidebar-header .logo-icon {
      display: block !important;
    }
  }
</style>
